import React, { useEffect, useState } from "react";
import { credibilityHistory } from "@/api";
import { THEORY } from "@/constants/testIds";

// 8-cell Unicode block ramp, lowest → highest.
const BLOCKS = ["▁", "▂", "▃", "▄", "▅", "▆", "▇", "█"];

function buildSparkline(scores) {
  if (!scores.length) return "";
  const min = Math.min(...scores);
  const max = Math.max(...scores);
  if (max === min) {
    // Flat line — show a mid-level block for every sample.
    return BLOCKS[3].repeat(scores.length);
  }
  return scores
    .map((s) => {
      const norm = (s - min) / (max - min); // 0..1
      const idx = Math.min(BLOCKS.length - 1, Math.round(norm * (BLOCKS.length - 1)));
      return BLOCKS[idx];
    })
    .join("");
}

const CredibilityTrend = ({ slug, days = 30 }) => {
  const [history, setHistory] = useState(null); // null = loading, [] = empty
  const [err, setErr] = useState(false);

  useEffect(() => {
    let alive = true;
    credibilityHistory(slug, days)
      .then((data) => {
        if (alive) setHistory(data.history || []);
      })
      .catch(() => {
        if (alive) setErr(true);
      });
    return () => {
      alive = false;
    };
  }, [slug, days]);

  if (err || history === null) return null;

  const usable = history.filter((e) => typeof e.score === "number");

  if (usable.length < 2) {
    return (
      <div
        data-testid={THEORY.credibilityTrend}
        className="cred-trend"
        style={{
          fontSize: 12,
          letterSpacing: "0.08em",
          marginTop: "0.35rem",
          color: "var(--ax-muted, #6e7d70)",
        }}
      >
        <span style={{ color: "var(--ax-accent)" }}>CREDIBILITY TREND</span>{" "}
        <span>[INSUFFICIENT DATA — FIRST SNAPSHOT PENDING]</span>
      </div>
    );
  }

  const scores = usable.map((e) => e.score);
  const sparkline = buildSparkline(scores);
  const delta = scores[scores.length - 1] - scores[0];
  const deltaRounded = Math.round(delta);
  const deltaSign = delta >= 0 ? "+" : "-";
  const deltaColor = delta >= 0 ? "var(--ax-accent)" : "#ffb800";

  return (
    <div
      data-testid={THEORY.credibilityTrend}
      className="cred-trend"
      style={{
        fontSize: 12,
        letterSpacing: "0.08em",
        marginTop: "0.35rem",
        display: "flex",
        flexWrap: "wrap",
        alignItems: "baseline",
        gap: "0.6rem",
      }}
    >
      <span style={{ color: "var(--ax-accent)" }}>
        CREDIBILITY TREND [{days}d]
      </span>
      <span
        style={{
          color: "var(--ax-accent)",
          fontSize: 16,
          letterSpacing: "0.02em",
        }}
      >
        {sparkline}
      </span>
      <span style={{ color: deltaColor, fontWeight: 700 }}>
        {deltaSign}
        {Math.abs(deltaRounded)} pts
      </span>
    </div>
  );
};

export default CredibilityTrend;
